import React, { useEffect, useRef, useState } from 'react';
import { webOrigin } from '../utils/brand';
import { shareImage as shareImageFile, saveImage } from '../utils/nativeShare';
import { createPortal } from 'react-dom';
import * as htmlToImage from 'html-to-image';
import SignalStoryCard from './SignalStoryCard';
import SignalFeedCard from './SignalFeedCard';
import useShareTemplates from '../hooks/useShareTemplates';
import { NO_WORDS_HEADLINE, openedLine } from '../utils/portalWords';

// Signals are cyan across the app.
const SIGNAL_ACCENT = '#22ECFB';

function ensureFont() {
  if (document.getElementById('pr-courier-prime')) return;
  const l = document.createElement('link');
  l.id = 'pr-courier-prime';
  l.rel = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap';
  document.head.appendChild(l);
}

function fmtWindow(startIso, endIso) {
  if (!startIso) return '';
  const s = new Date(startIso);
  const day = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
  const time = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (!endIso) return `${day(s)} · ${time(s)}`;
  const e = new Date(endIso);
  return `${day(s)} · ${time(s)} – ${time(e)}`;
}

/**
 * The Signal share moment. Shows a live preview of the 1080×1920 Story card and
 * offers to share it as an image (→ Instagram Story / socials / messages via the
 * native sheet), copy the link, or download the PNG. Matches the "confirmation
 * sheet" in the Signal Share Cards spec.
 */
export default function SignalShareModal({ signal, onClose }) {
  const captureRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [format, setFormat] = useState('story'); // 'story' (9:16) | 'feed' (1:1)
  const isFeed = format === 'feed';

  useEffect(() => { ensureFont(); }, []);

  const defaults = useShareTemplates();
  const name = (signal.hostDisplayName || 'Someone').trim();
  const card = {
    accent: SIGNAL_ACCENT,
    init: (name[0] || '?').toUpperCase(),
    topline: `${name} `,
    keyword: 'OPENED A PORTAL',
    template: signal.shareTemplate || defaults?.signal || 'ring',
    words: signal.signalText || NO_WORDS_HEADLINE,
    meta: [signal.locationLabel || signal.locationCity, fmtWindow(signal.dateTimeUtc, signal.expiresAtUtc)].filter(Boolean).join(' · '),
    closer: 'COME FIND ME →',
  };
  const url = `${webOrigin()}/portals/${signal.eventId}`;
  const shareText = signal.signalText ? `${name}: “${card.words}” — join me on Portal` : `${openedLine(name)} — join me on Portal`;

  const makeBlob = async () => {
    ensureFont();
    try { await document.fonts.load("700 33px 'Courier Prime'"); await document.fonts.ready; } catch { /* fonts optional */ }
    await new Promise((r) => setTimeout(r, 80)); // let gradients/fonts settle
    return htmlToImage.toBlob(captureRef.current, { width: 1080, height: isFeed ? 1080 : 1920, pixelRatio: 1, cacheBust: true, backgroundColor: '#000' });
  };

  // Native has no download; saveImage routes to the OS sheet, where
  // "Save Image" lives. On the web it's still a plain download.
  const downloadBlob = (blob) => saveImage({ blob, fileName: 'party-resume-signal.png' });

  const shareImage = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await makeBlob();
      if (!blob) throw new Error('render failed');
      // navigator.share is absent in Android's WebView and inert in a
      // plain WKWebView; shareImageFile asks the OS directly when native.
      await shareImageFile({ blob, fileName: 'party-resume-signal.png', title: 'Portal', text: shareText, url });
    } catch { /* user cancelled / render failed */ }
    setBusy(false);
  };

  const download = async () => {
    if (busy) return;
    setBusy(true);
    try { const blob = await makeBlob(); if (blob) downloadBlob(blob); } catch { /* ignore */ }
    setBusy(false);
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`, '_blank', 'noopener');
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* blocked */ }
  };

  const btn = 'h-12 border-2 text-[13px] font-bold uppercase tracking-[0.2em] transition-opacity disabled:opacity-60';

  return createPortal(
    <div className="pr-overlay-root fixed inset-0 safe-inset z-[80] flex items-end justify-center bg-black/70 sm:items-center" onClick={onClose}>
      <div
        className="w-full max-w-sm border-t-2 border-black bg-black p-6 sm:border-2 sm:border-white/20"
        style={{ fontFamily: "'Courier Prime', ui-monospace, monospace" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase tracking-[0.32em]" style={{ color: SIGNAL_ACCENT }}>[ Live ]</div>
          <div className="flex border-2 border-white/20">
            {[['story', 'Story'], ['feed', 'Feed']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setFormat(k)}
                className="px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest transition-colors"
                style={format === k ? { background: SIGNAL_ACCENT, color: '#000' } : { color: '#fff' }}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-2 text-[15px] font-bold uppercase leading-snug text-white">Portal&apos;s up.<br />Light the sky?</div>

        <div className="my-5 flex justify-center">
          <div style={{ width: 200, height: isFeed ? 200 : 356, overflow: 'hidden', outline: '1px solid #333' }}>
            <div style={{ transform: 'scale(0.18518)', transformOrigin: 'top left' }}>
              {isFeed ? <SignalFeedCard {...card} /> : <SignalStoryCard {...card} />}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <button type="button" onClick={shareImage} disabled={busy} className={`${btn} text-black`} style={{ background: SIGNAL_ACCENT, borderColor: SIGNAL_ACCENT }}>
            {busy ? 'Rendering…' : (isFeed ? 'Share post' : 'Instagram Story')}
          </button>
          <button type="button" onClick={shareWhatsApp} className={`${btn} border-white bg-transparent text-white`}>
            WhatsApp
          </button>
          <button type="button" onClick={copyLink} className={`${btn} border-white/40 bg-transparent text-white/80`}>
            {copied ? 'Copied' : 'Copy Link'}
          </button>
          <button type="button" onClick={download} disabled={busy} className={`${btn} border-white/40 bg-transparent text-white/80`}>
            Download Image
          </button>
        </div>
        <button type="button" onClick={onClose} className="mt-4 w-full text-center text-[13px] tracking-wide text-white/45 hover:text-white">not now</button>
      </div>

      {/* Off-screen full-resolution node used for rasterizing the chosen image. */}
      <div style={{ position: 'fixed', left: -99999, top: 0, pointerEvents: 'none' }} aria-hidden>
        {isFeed ? <SignalFeedCard {...card} refEl={captureRef} /> : <SignalStoryCard {...card} refEl={captureRef} />}
      </div>
    </div>,
    document.body,
  );
}
